import { useCallback, useEffect, useRef } from 'react';

const DEBOUNCE_MS = 500;

export function useDraft(key: string) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValueRef = useRef<string | null>(null);
  const pendingKeyRef = useRef<string>(key);

  // Flush any pending debounced write immediately
  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingValueRef.current !== null) {
      const targetKey = pendingKeyRef.current;
      const value = pendingValueRef.current;
      pendingValueRef.current = null;
      try {
        if (!value || value === '<p></p>') {
          localStorage.removeItem(targetKey);
        } else {
          localStorage.setItem(targetKey, value);
        }
      } catch {
        // localStorage full or blocked — fail silently
      }
    }
  }, []);

  // When the key changes (e.g. conversation switch), flush pending write
  // to the *previous* key before switching context
  useEffect(() => {
    flush();
    pendingKeyRef.current = key;
  }, [key, flush]);

  // Flush on unmount so recent typing is never lost
  useEffect(() => {
    return () => {
      flush();
    };
  }, [flush]);

  const read = useCallback((): string => {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  }, [key]);

  const write = useCallback((value: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingValueRef.current = value;
    pendingKeyRef.current = key;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      pendingValueRef.current = null;
      try {
        if (!value || value === '<p></p>') {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, value);
        }
      } catch {
        // localStorage full or blocked — fail silently
      }
    }, DEBOUNCE_MS);
  }, [key]);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingValueRef.current = null;
    try {
      localStorage.removeItem(key);
    } catch { /* localStorage blocked — fail silently */ }
  }, [key]);

  return { read, write, clear };
}
